import { Box, Stack, Typography, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { getAdminPasswordPolicy, getPasswordRuleStatuses, type PasswordPolicy } from '../../lib/adminPasswordPolicy';

type PasswordRequirementsProps = {
  password: string;
  policy?: PasswordPolicy;
  /** When true, unsatisfied rules use error styling (after a failed submit). */
  highlightFailures?: boolean;
};

export function PasswordRequirements({
  password,
  policy = getAdminPasswordPolicy(),
  highlightFailures = false,
}: PasswordRequirementsProps) {
  const theme = useTheme();
  const rules = getPasswordRuleStatuses(password, policy);

  return (
    <Box
      data-testid="admin-password-requirements"
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'action.hover',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
        Password requirements
      </Typography>
      <Stack spacing={0.5}>
        {rules.map((rule) => (
          <Stack key={rule.id} direction="row" spacing={1} alignItems="flex-start">
            {rule.satisfied ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main', mt: '2px' }} />
            ) : (
              <RadioButtonUncheckedIcon
                sx={{
                  fontSize: 16,
                  color: highlightFailures ? 'error.main' : 'text.disabled',
                  mt: '2px',
                }}
              />
            )}
            <Typography
              variant="caption"
              color={rule.satisfied ? 'success.main' : highlightFailures ? 'error.main' : 'text.secondary'}
              sx={{ lineHeight: 1.45 }}
            >
              {rule.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
